/**
 * Selector de sede activa para usuarios **multi-sede** (rol `admin` o permiso
 * `all`). Los usuarios de una sola sede no lo ven: operan con su sede por
 * defecto, así que el componente no se renderiza para ellos.
 *
 * Al elegir una sede llama a `setActiveSede` del contexto, que fija el store
 * `lib/sede` (lo que hace que `lib/api` reenvíe `X-Sede-Id` en las siguientes
 * peticiones) y revalida `/auth/me`. Los datos del catálogo llegan de `useSedes`
 * (TanStack Query); el componente solo presenta.
 */
import { useAuth } from "@/lib/auth";

import { useSedes } from "../hooks/useSedes";

export function SedeSelector() {
  const { isMultiSede, activeSede, setActiveSede } = useAuth();
  const sedesQuery = useSedes(isMultiSede);

  // Solo el usuario multi-sede elige sede; el de sede única usa la suya.
  if (!isMultiSede) {
    return null;
  }

  const labelId = "sede-selector-label";

  return (
    <div className="flex items-center gap-2">
      <label id={labelId} htmlFor="sede-selector" className="text-sm font-medium text-slate-700">
        Sede
      </label>
      <select
        id="sede-selector"
        aria-labelledby={labelId}
        value={activeSede?.id ?? ""}
        disabled={sedesQuery.isPending || sedesQuery.isError}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (!Number.isNaN(next)) {
            setActiveSede(next);
          }
        }}
        className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60"
      >
        {/* Placeholder cuando el multi-sede aún no eligió (active_sede null). */}
        {activeSede === null && (
          <option value="" disabled>
            Selecciona una sede…
          </option>
        )}
        {(sedesQuery.data ?? []).map((sede) => (
          <option key={sede.id} value={sede.id}>
            {sede.nombre}
          </option>
        ))}
      </select>
      {sedesQuery.isError && (
        <span role="alert" className="text-sm text-red-600">
          No se pudieron cargar las sedes.
        </span>
      )}
    </div>
  );
}
